import { useEffect, useState } from 'react';
import { useToast } from '@nfp-tracker/components';
import { supabase } from '../integrations/supabase/client';
import { useOwnerContext } from './useOwnerContext';
import { handleExternalError } from '../utils/errorSnackbar';
import { computeSubscriptionStatus } from '../utils/subscriptionStatus';
import {
  hasSubscriptionBootstrapCompleted,
  subscribeToSubscriptionBootstrap,
} from '../utils/subscriptionBootstrap';

interface SubscriptionStatus {
  hasAccess: boolean;
  status: string | null;
  daysRemaining: number | null;
  isGracePeriod: boolean;
  trialEndDate: string | null;
  subscriptionEndDate: string | null;
  subscriptionRenewalDate: string | null;
  subscriptionAutoRenews: boolean | null;
  freeForLife: boolean;
  planKey: string | null;
  stripePriceId: string | null;
  billingProvider: string | null;
  appStoreProductId: string | null;
  appStoreEnvironment: string | null;
}

type SubscriptionViewRow = {
  subscription_status: string | null;
  trial_end_date: string | null;
  subscription_end_date: string | null;
  subscription_renewal_date: string | null;
  subscription_auto_renews: boolean | null;
  stripe_price_id: string | null;
  stripe_product_id: string | null;
  plan_key: string | null;
  billing_provider: string | null;
  app_store_product_id: string | null;
  app_store_environment: string | null;
};

const subscriptionCache = new Map<string, SubscriptionStatus>();

const isSubscriptionHydrationPending = (
  subscriptionData: SubscriptionViewRow | null
): boolean => {
  if (!subscriptionData) {
    return true;
  }

  return !subscriptionData.subscription_status
    && !subscriptionData.trial_end_date
    && !subscriptionData.subscription_end_date
    && !subscriptionData.subscription_renewal_date
    && subscriptionData.subscription_auto_renews == null
    && !subscriptionData.plan_key
    && !subscriptionData.stripe_price_id
    && !subscriptionData.stripe_product_id
    && !subscriptionData.billing_provider
    && !subscriptionData.app_store_product_id
    && !subscriptionData.app_store_environment;
};

const defaultSubscriptionStatus: SubscriptionStatus = {
  hasAccess: true,
  status: null,
  daysRemaining: null,
  isGracePeriod: false,
  trialEndDate: null,
  subscriptionEndDate: null,
  subscriptionRenewalDate: null,
  subscriptionAutoRenews: null,
  freeForLife: false,
  planKey: null,
  stripePriceId: null,
  billingProvider: null,
  appStoreProductId: null,
  appStoreEnvironment: null,
};

export const useSubscription = (coupleId: string | undefined) => {
  // Instructor accounts are not billed, and they cannot read a client's subscription row
  // anyway — so gating them on one would lock them out of every chart shared with them.
  const ownerContext = useOwnerContext().data;
  const isInstructorAccount =
    ownerContext?.status === 'ready' && ownerContext.actorRole === 'instructor';

  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>(() => {
    if (!coupleId) return defaultSubscriptionStatus;
    return subscriptionCache.get(coupleId) ?? defaultSubscriptionStatus;
  });
  const [loading, setLoading] = useState(() => {
    if (!coupleId) return false;
    return !subscriptionCache.has(coupleId);
  });
  const { toast } = useToast();

  useEffect(() => {
    if (!coupleId || isInstructorAccount) {
      setSubscriptionStatus(defaultSubscriptionStatus);
      setLoading(false);
      return;
    }

    const cachedStatus = subscriptionCache.get(coupleId);
    if (cachedStatus) {
      setSubscriptionStatus(cachedStatus);
      setLoading(false);
    } else {
      setLoading(true);
    }

    const checkSubscription = async () => {
      let keepLoading = false;
      try {
        const { data, error } = await supabase
          .from('my_subscription_view')
          .select('subscription_status, trial_end_date, subscription_end_date, subscription_renewal_date, subscription_auto_renews, stripe_price_id, stripe_product_id, plan_key, billing_provider, app_store_product_id, app_store_environment')
          .eq('owner_user_id', coupleId)
          .maybeSingle();

        if (error) throw error;

        const subscriptionData = data as SubscriptionViewRow | null;
        const bootstrapCompleted = hasSubscriptionBootstrapCompleted(coupleId);

        if (isSubscriptionHydrationPending(subscriptionData) && !bootstrapCompleted) {
          keepLoading = true;
          setLoading(true);
          return;
        }

        if (!subscriptionData) {
          setSubscriptionStatus({
            hasAccess: false,
            status: null,
            daysRemaining: null,
            isGracePeriod: false,
            trialEndDate: null,
            subscriptionEndDate: null,
            subscriptionRenewalDate: null,
            subscriptionAutoRenews: null,
            freeForLife: false,
            planKey: null,
            stripePriceId: null,
            billingProvider: null,
            appStoreProductId: null,
            appStoreEnvironment: null,
          });
          return;
        }

        const computedStatus = computeSubscriptionStatus({
          subscriptionStatus: subscriptionData.subscription_status,
          trialEndDate: subscriptionData.trial_end_date,
          subscriptionEndDate: subscriptionData.subscription_end_date,
          planKey: subscriptionData.plan_key,
          now: new Date(),
        });

        const nextStatus: SubscriptionStatus = {
          hasAccess: computedStatus.hasAccess,
          status: computedStatus.status,
          daysRemaining: computedStatus.daysRemaining,
          isGracePeriod: computedStatus.isGracePeriod,
          trialEndDate: subscriptionData.trial_end_date,
          subscriptionEndDate: subscriptionData.subscription_end_date,
          subscriptionRenewalDate: subscriptionData.subscription_renewal_date,
          subscriptionAutoRenews: subscriptionData.subscription_auto_renews,
          freeForLife: computedStatus.freeForLife,
          planKey: subscriptionData.plan_key,
          stripePriceId: subscriptionData.stripe_price_id,
          billingProvider: subscriptionData.billing_provider,
          appStoreProductId: subscriptionData.app_store_product_id,
          appStoreEnvironment: subscriptionData.app_store_environment,
        };

        subscriptionCache.set(coupleId, nextStatus);
        setSubscriptionStatus(nextStatus);
      } catch (error) {
        // This polls every 60s in the background — a transient network blip shouldn't
        // nag the user with a toast or light up Sentry every minute until it self-heals.
        const isNetworkFailure = error instanceof TypeError && /fetch/i.test(error.message);
        if (isNetworkFailure) {
          console.error('Error checking subscription (network hiccup, will retry):', error);
        } else {
          handleExternalError(toast, error, {
            logPrefix: 'Error checking subscription:',
          });
        }
      } finally {
        setLoading(keepLoading ? true : false);
      }
    };

    checkSubscription();

    const channel = supabase
      .channel(`subscription-status-${coupleId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_subscriptions',
          filter: `owner_user_id=eq.${coupleId}`,
        },
        () => {
          void checkSubscription();
        }
      )
      .subscribe();
    const unsubscribeBootstrap = subscribeToSubscriptionBootstrap(coupleId, () => {
      void checkSubscription();
    });
    
    // Recheck every minute
    const interval = setInterval(checkSubscription, 60000);
    return () => {
      clearInterval(interval);
      unsubscribeBootstrap();
      supabase.removeChannel(channel);
    };
  }, [coupleId, isInstructorAccount, toast]);

  return { ...subscriptionStatus, loading };
};
